import * as THREE from 'three';
import { FikoBrepSurfaceRecognition } from './FikoBrepSurfaceRecognition';

/**
 * BREP Vertex (Köşe) - Topolojik nokta
 */
export interface BrepVertex {
    id: string;
    position: THREE.Vector3;
    tolerance: number; // geometrik tolerans (varsayılan: 1e-6)
    edges: string[]; // bu vertex'e bağlı edge'ler
}

/**
 * BREP Curve (Eğri) - Parametrik 3D eğri tipleri
 */
export type BrepCurve =
    | { type: 'line'; start: THREE.Vector3; end: THREE.Vector3; length: number }
    | { type: 'arc'; center: THREE.Vector3; radius: number; startAngle: number; endAngle: number; normal: THREE.Vector3; length: number }
    | { type: 'circle'; center: THREE.Vector3; radius: number; normal: THREE.Vector3; length: number }
    | { type: 'ellipse'; center: THREE.Vector3; majorAxis: THREE.Vector3; minorAxis: THREE.Vector3; length: number }
    | { type: 'spline'; controlPoints: THREE.Vector3[]; knots: number[]; degree: number; length: number }
    | { type: 'bezier'; controlPoints: THREE.Vector3[]; length: number }
    | { type: 'polyline'; points: THREE.Vector3[]; length: number };

/**
 * BREP Edge (Kenar) - İki vertex arasındaki parametrik eğri
 */
export interface BrepEdge {
    id: string;
    startVertex: string; // vertex ID
    endVertex: string;   // vertex ID
    curve: BrepCurve;    // geometrik eğri tanımı
    faces: string[];     // bu kenara bağlı face ID'leri (genellikle 1 veya 2)
    convex: boolean;     // dışbükey kenar mı (face normalleri arası açı < 180°)
    angle: number;       // komşu face'ler arası açı (derece)
    length: number;      // eğri uzunluğu
}

/**
 * BREP Loop (Döngü) - Sıralı edge'lerden oluşan kapalı döngü
 */
export interface BrepLoop {
    id: string;
    edges: string[];     // sıralı edge ID listesi (ccw yönünde)
    isOuter: boolean;    // dış döngü (outer) mu, delik (hole) mi
    vertices: string[];  // döngüdeki vertex'ler (edge sırasına göre)
}

/**
 * BREP Surface (Yüzey) - Parametrik yüzey tipleri
 */
export type BrepSurface =
    | { type: 'plane'; origin: THREE.Vector3; normal: THREE.Vector3; uAxis: THREE.Vector3; vAxis: THREE.Vector3 }
    | { type: 'cylinder'; axis: THREE.Vector3; radius: number; center: THREE.Vector3; height: number }
    | { type: 'sphere'; center: THREE.Vector3; radius: number }
    | { type: 'cone'; apex: THREE.Vector3; axis: THREE.Vector3; radius: number; angle: number; height?: number }
    | { type: 'torus'; center: THREE.Vector3; axis: THREE.Vector3; majorRadius: number; minorRadius: number }
    | { type: 'nurbs'; controlPoints: THREE.Vector3[][]; knotsU: number[]; knotsV: number[]; degreeU: number; degreeV: number }
    | { type: 'revolution'; profile: BrepCurve; axis: THREE.Vector3; angle: number }
    | { type: 'extrusion'; profile: BrepCurve; direction: THREE.Vector3; distance: number }
    | { type: 'ruled'; curve1: BrepCurve; curve2: BrepCurve }
    | { type: 'freeform'; triangles: THREE.Triangle[] }; // düzensiz mesh yüzeyleri için

/**
 * BREP Face (Yüzey) - Parametrik yüzey + boundary loops
 */
export interface BrepFace {
    id: string;
    surface: BrepSurface;    // parametrik yüzey tanımı
    loops: BrepLoop[];       // outer loop (ilk) + holes
    edges: string[];         // tüm edge ID'leri
    normal: THREE.Vector3;   // yüzey normali (ortalama)
    area: number;            // yüzey alanı
    centroid: THREE.Vector3; // ağırlık merkezi
    color?: THREE.Color;     // renk bilgisi (varsa)
    material?: any;          // material bilgisi (varsa)
}

/**
 * BREP Shell (Kabuk) - Bağlı face'lerden oluşan kapalı/açık kabuk
 */
export interface BrepShell {
    id: string;
    faces: string[];         // face ID'leri
    isClosed: boolean;       // kapalı kabuk mu (su geçirmez)
    volume?: number;         // kapalıysa hacim hesaplanabilir
    boundingBox: THREE.Box3; // sınırlayıcı kutu
}

/**
 * BREP Solid (Katı Cisim) - Bir veya daha fazla shell'den oluşan katı
 */
export interface BrepSolid {
    id: string;
    outerShell: string;      // dış shell ID
    voids: string[];         // iç boşluklar (void shells)
    volume: number;          // katı hacmi
    centerOfMass: THREE.Vector3; // ağırlık merkezi
    boundingBox: THREE.Box3;
}

/**
 * BREP Body (Gövde) - Tüm topolojik veri yapısı
 */
export interface BrepBody {
    // Topolojik elemanlar
    vertices: Map<string, BrepVertex>;
    edges: Map<string, BrepEdge>;
    loops: Map<string, BrepLoop>;
    faces: Map<string, BrepFace>;
    shells: Map<string, BrepShell>;
    solids: Map<string, BrepSolid>;

    // Metadata
    tolerance: number;       // global geometrik tolerans
    boundingBox: THREE.Box3;

    // İstatistikler
    stats: {
        vertexCount: number;
        edgeCount: number;
        faceCount: number;
        shellCount: number;
        solidCount: number;
        totalVolume: number;
        totalArea: number;
    };
}

/**
 * Mesh-to-BREP Analiz Sonucu
 */
export interface MeshAnalysisResult {
    brep: BrepBody;

    // Tespit edilen feature'lar
    features: {
        planes: BrepFace[];      // düzlem yüzeyler
        cylinders: BrepFace[];   // silindirik yüzeyler
        spheres: BrepFace[];     // küresel yüzeyler
        cones: BrepFace[];       // konik yüzeyler
        tori: BrepFace[];        // halka (torus) yüzeyler
        freeform: BrepFace[];    // serbest form yüzeyler
    };

    // Topolojik analiz
    topology: {
        manifold: boolean;       // manifold mesh mi (her edge max 2 face)
        watertight: boolean;     // su geçirmez mi
        orientable: boolean;     // yönlendirilebilir mi
        eulerCharacteristic: number; // V - E + F (topolojik invaryant)
        genus: number;           // topolojik genus (delik sayısı)
    };

    // Geometrik özellikler
    geometry: {
        convexEdges: BrepEdge[];
        concaveEdges: BrepEdge[];
        sharpEdges: BrepEdge[];  // açı > 30°
        smoothEdges: BrepEdge[]; // açı < 10°
    };
}

/**
 * FikoBrep - Gelişmiş Mesh-to-BREP Analiz ve Dönüşüm Sistemi
 *
 * Bu sınıf:
 * - Mesh geometrisini BREP topolojisine dönüştürür
 * - Yüzey/eğri tiplerini otomatik tanır (plane, cylinder, sphere, etc.)
 * - Topolojik ilişkileri çıkarır (vertex-edge-face bağlantıları)
 * - STEP/IGES formatlarında kullanılan BREP yapısını oluşturur
 * - FikoWasmBridge ile WASM-accelerated analiz yapar
 */
export class FikoBrep {
    private tolerance: number = 1e-6; // geometrik tolerans

    /**
     * Mesh geometrisinden BREP topolojisi çıkarır (WASM-accelerated)
     */
    public async meshtenBrepOlustur(
        meshGeom: THREE.BufferGeometry,
        options?: {
            tolerance?: number;
            recognizeSurfaces?: boolean; // yüzey tanıma yapılsın mı
            recognizeCurves?: boolean;   // eğri tanıma yapılsın mı
            simplify?: boolean;          // basitleştirme yapılsın mı
            mergeCoplanarFaces?: boolean; // eş düzlemli yüzeyler birleştirilsin mi
            useGroups?: boolean;         // grup attribute'larını kullan
        }
    ): Promise<MeshAnalysisResult> {
        // WASM-accelerated analyzer kullan
        const { FikoBrepWasmAnalyzer } = await import('./FikoBrepWasmAnalyzer');
        const analyzer = new FikoBrepWasmAnalyzer(options?.tolerance ?? this.tolerance);

        return await analyzer.analyzeMesh(meshGeom, {
            tolerance: options?.tolerance,
            useGroups: options?.useGroups ?? true,
            recognizeSurfaces: options?.recognizeSurfaces ?? true
        });
    }

    /**
     * Vertex'leri çıkarır ve duplicate'leri birleştirir
     */
    private extractVertices(meshGeom: THREE.BufferGeometry, brep: BrepBody): void {
        const positions = meshGeom.attributes['position'];
        if (!positions) return;

        const vertexMap = new Map<string, string>(); // position hash -> vertex ID
        let vertexId = 0;

        for (let i = 0; i < positions.count; i++) {
            const pos = new THREE.Vector3(
                positions.getX(i),
                positions.getY(i),
                positions.getZ(i)
            );

            // Position hash oluştur (tolerance ile)
            const hash = this.positionHash(pos);

            if (!vertexMap.has(hash)) {
                const id = `V${vertexId++}`;
                vertexMap.set(hash, id);

                brep.vertices.set(id, {
                    id,
                    position: pos,
                    tolerance: this.tolerance,
                    edges: []
                });
            }
        }

        brep.stats.vertexCount = brep.vertices.size;
    }

    /**
     * Edge'leri çıkarır (duplicate edge'leri birleştirir, komşu face'leri tespit eder)
     */
    private extractEdges(meshGeom: THREE.BufferGeometry, brep: BrepBody): void {
        const positions = meshGeom.attributes['position'];
        if (!positions) return;

        const edgeMap = new Map<string, BrepEdge>(); // edge hash -> edge
        let edgeId = 0;

        // Triangle'ları dolaş
        const triangleCount = meshGeom.index ? meshGeom.index.count / 3 : positions.count / 3;

        for (let i = 0; i < triangleCount; i++) {
            // Triangle vertex'leri
            const i0 = meshGeom.index ? meshGeom.index.getX(i * 3 + 0) : i * 3 + 0;
            const i1 = meshGeom.index ? meshGeom.index.getX(i * 3 + 1) : i * 3 + 1;
            const i2 = meshGeom.index ? meshGeom.index.getX(i * 3 + 2) : i * 3 + 2;

            // 3 edge oluştur
            this.addEdge(edgeMap, brep, i0, i1, `F${i}`, () => `E${edgeId++}`);
            this.addEdge(edgeMap, brep, i1, i2, `F${i}`, () => `E${edgeId++}`);
            this.addEdge(edgeMap, brep, i2, i0, `F${i}`, () => `E${edgeId++}`);
        }

        // Edge'leri brep'e ekle
        edgeMap.forEach(edge => brep.edges.set(edge.id, edge));
        brep.stats.edgeCount = brep.edges.size;
    }

    /**
     * Tek bir edge ekler (duplicate kontrolü ile)
     */
    private addEdge(
        edgeMap: Map<string, BrepEdge>,
        brep: BrepBody,
        v0Idx: number,
        v1Idx: number,
        faceId: string,
        idGenerator: () => string
    ): void {
        const positions = brep.vertices;

        // Vertex ID'lerini bul
        const v0Hash = this.positionHashByIndex(v0Idx, brep);
        const v1Hash = this.positionHashByIndex(v1Idx, brep);

        const v0Id = this.findVertexByHash(v0Hash, brep);
        const v1Id = this.findVertexByHash(v1Hash, brep);

        if (!v0Id || !v1Id) return;

        // Edge hash (sırasız - v0-v1 ve v1-v0 aynı)
        const edgeHash = this.edgeHash(v0Id, v1Id);

        if (edgeMap.has(edgeHash)) {
            // Mevcut edge'e face ekle
            edgeMap.get(edgeHash)!.faces.push(faceId);
        } else {
            // Yeni edge oluştur
            const v0 = brep.vertices.get(v0Id)!;
            const v1 = brep.vertices.get(v1Id)!;

            const curve: BrepCurve = {
                type: 'line',
                start: v0.position.clone(),
                end: v1.position.clone(),
                length: v0.position.distanceTo(v1.position)
            };

            const edge: BrepEdge = {
                id: idGenerator(),
                startVertex: v0Id,
                endVertex: v1Id,
                curve,
                faces: [faceId],
                convex: true, // sonra hesaplanacak
                angle: 0,
                length: curve.length
            };

            edgeMap.set(edgeHash, edge);

            // Vertex'e edge ekle
            v0.edges.push(edge.id);
            v1.edges.push(edge.id);
        }
    }

    /**
     * Face'leri çıkarır (opsiyonel: eş düzlemli face'leri birleştirir)
     */
    private extractFaces(meshGeom: THREE.BufferGeometry, brep: BrepBody, merge: boolean): void {
        const positions = meshGeom.attributes['position'];
        const normals = meshGeom.attributes['normal'];
        const groups = meshGeom.attributes['grup']; // BRep grup attribute'u

        if (!positions) return;

        const triangleCount = meshGeom.index ? meshGeom.index.count / 3 : positions.count / 3;
        let faceId = 0;

        // Eğer grup attribute'u varsa, grup bazlı face oluştur
        if (groups) {
            const groupFaces = new Map<number, { triangles: number[]; normal: THREE.Vector3 }>();

            for (let i = 0; i < triangleCount; i++) {
                // Grup ID'yi al (grup attribute formatı: [start_idx, count, group_id])
                const triStart = meshGeom.index ? meshGeom.index.getX(i * 3) : i * 3;
                const groupId = groups.getZ ? groups.getZ(triStart) : 0;

                if (!groupFaces.has(groupId)) {
                    groupFaces.set(groupId, { triangles: [], normal: new THREE.Vector3() });
                }
                groupFaces.get(groupId)!.triangles.push(i);

                // Normal hesapla
                if (normals) {
                    const n = new THREE.Vector3(
                        normals.getX(triStart),
                        normals.getY(triStart),
                        normals.getZ(triStart)
                    );
                    groupFaces.get(groupId)!.normal.add(n);
                }
            }

            // Her grup için face oluştur
            groupFaces.forEach((data, groupId) => {
                const id = `F${faceId++}`;
                const avgNormal = data.normal.normalize();

                // Face'in centroid'ini hesapla
                const centroid = new THREE.Vector3();
                let area = 0;

                data.triangles.forEach(triIdx => {
                    const i0 = meshGeom.index ? meshGeom.index.getX(triIdx * 3) : triIdx * 3;
                    const i1 = meshGeom.index ? meshGeom.index.getX(triIdx * 3 + 1) : triIdx * 3 + 1;
                    const i2 = meshGeom.index ? meshGeom.index.getX(triIdx * 3 + 2) : triIdx * 3 + 2;

                    const p0 = new THREE.Vector3(positions.getX(i0), positions.getY(i0), positions.getZ(i0));
                    const p1 = new THREE.Vector3(positions.getX(i1), positions.getY(i1), positions.getZ(i1));
                    const p2 = new THREE.Vector3(positions.getX(i2), positions.getY(i2), positions.getZ(i2));

                    centroid.add(p0).add(p1).add(p2);

                    // Triangle alanı
                    const edge1 = p1.clone().sub(p0);
                    const edge2 = p2.clone().sub(p0);
                    area += edge1.cross(edge2).length() * 0.5;
                });

                centroid.divideScalar(data.triangles.length * 3);

                const face: BrepFace = {
                    id,
                    surface: { type: 'freeform', triangles: [] }, // Sonra tanınacak
                    loops: [],
                    edges: [],
                    normal: avgNormal,
                    area,
                    centroid
                };

                brep.faces.set(id, face);
            });
        } else if (merge) {
            // Eş düzlemli face'leri birleştir (region growing)
            this.extractFacesWithRegionGrowing(meshGeom, brep);
        } else {
            // Her triangle ayrı face
            for (let i = 0; i < triangleCount; i++) {
                const id = `F${faceId++}`;

                const i0 = meshGeom.index ? meshGeom.index.getX(i * 3) : i * 3;
                const i1 = meshGeom.index ? meshGeom.index.getX(i * 3 + 1) : i * 3 + 1;
                const i2 = meshGeom.index ? meshGeom.index.getX(i * 3 + 2) : i * 3 + 2;

                const p0 = new THREE.Vector3(positions.getX(i0), positions.getY(i0), positions.getZ(i0));
                const p1 = new THREE.Vector3(positions.getX(i1), positions.getY(i1), positions.getZ(i1));
                const p2 = new THREE.Vector3(positions.getX(i2), positions.getY(i2), positions.getZ(i2));

                const edge1 = p1.clone().sub(p0);
                const edge2 = p2.clone().sub(p0);
                const normal = edge1.cross(edge2).normalize();
                const area = edge1.cross(edge2).length() * 0.5;
                const centroid = p0.add(p1).add(p2).divideScalar(3);

                const face: BrepFace = {
                    id,
                    surface: { type: 'freeform', triangles: [new THREE.Triangle(p0, p1, p2)] },
                    loops: [],
                    edges: [],
                    normal,
                    area,
                    centroid
                };

                brep.faces.set(id, face);
            }
        }

        brep.stats.faceCount = brep.faces.size;
    }

    /**
     * Region growing ile eş düzlemli face'leri birleştir
     */
    private extractFacesWithRegionGrowing(meshGeom: THREE.BufferGeometry, brep: BrepBody): void {
        const positions = meshGeom.attributes['position'];
        const triangleCount = meshGeom.index ? meshGeom.index.count / 3 : positions.count / 3;

        const visited = new Set<number>();
        const normalTolerance = Math.cos(15 * Math.PI / 180); // 15 derece tolerans
        let faceId = 0;

        // Triangle normal'lerini hesapla
        const triangleNormals: THREE.Vector3[] = [];
        const triangleCentroids: THREE.Vector3[] = [];

        for (let i = 0; i < triangleCount; i++) {
            const i0 = meshGeom.index ? meshGeom.index.getX(i * 3) : i * 3;
            const i1 = meshGeom.index ? meshGeom.index.getX(i * 3 + 1) : i * 3 + 1;
            const i2 = meshGeom.index ? meshGeom.index.getX(i * 3 + 2) : i * 3 + 2;

            const p0 = new THREE.Vector3(positions.getX(i0), positions.getY(i0), positions.getZ(i0));
            const p1 = new THREE.Vector3(positions.getX(i1), positions.getY(i1), positions.getZ(i1));
            const p2 = new THREE.Vector3(positions.getX(i2), positions.getY(i2), positions.getZ(i2));

            const edge1 = p1.clone().sub(p0);
            const edge2 = p2.clone().sub(p0);
            triangleNormals.push(edge1.cross(edge2).normalize());
            triangleCentroids.push(p0.clone().add(p1).add(p2).divideScalar(3));
        }

        // Komşuluk map'i oluştur (edge paylaşan triangle'lar)
        const adjacency = this.buildTriangleAdjacency(meshGeom);

        // Region growing
        for (let seed = 0; seed < triangleCount; seed++) {
            if (visited.has(seed)) continue;

            const region: number[] = [];
            const queue = [seed];
            const seedNormal = triangleNormals[seed];

            while (queue.length > 0) {
                const current = queue.shift()!;
                if (visited.has(current)) continue;

                const currentNormal = triangleNormals[current];
                if (currentNormal.dot(seedNormal) < normalTolerance) continue;

                visited.add(current);
                region.push(current);

                // Komşuları ekle
                const neighbors = adjacency.get(current) || [];
                neighbors.forEach(n => {
                    if (!visited.has(n)) {
                        queue.push(n);
                    }
                });
            }

            if (region.length > 0) {
                // Face oluştur
                const id = `F${faceId++}`;
                const avgNormal = new THREE.Vector3();
                const centroid = new THREE.Vector3();
                let totalArea = 0;

                region.forEach(triIdx => {
                    avgNormal.add(triangleNormals[triIdx]);
                    centroid.add(triangleCentroids[triIdx]);

                    // Alan hesapla
                    const i0 = meshGeom.index ? meshGeom.index.getX(triIdx * 3) : triIdx * 3;
                    const i1 = meshGeom.index ? meshGeom.index.getX(triIdx * 3 + 1) : triIdx * 3 + 1;
                    const i2 = meshGeom.index ? meshGeom.index.getX(triIdx * 3 + 2) : triIdx * 3 + 2;

                    const p0 = new THREE.Vector3(positions.getX(i0), positions.getY(i0), positions.getZ(i0));
                    const p1 = new THREE.Vector3(positions.getX(i1), positions.getY(i1), positions.getZ(i1));
                    const p2 = new THREE.Vector3(positions.getX(i2), positions.getY(i2), positions.getZ(i2));

                    const edge1 = p1.clone().sub(p0);
                    const edge2 = p2.clone().sub(p0);
                    totalArea += edge1.cross(edge2).length() * 0.5;
                });

                avgNormal.normalize();
                centroid.divideScalar(region.length);

                const face: BrepFace = {
                    id,
                    surface: { type: 'freeform', triangles: [] },
                    loops: [],
                    edges: [],
                    normal: avgNormal,
                    area: totalArea,
                    centroid
                };

                brep.faces.set(id, face);
            }
        }
    }

    /**
     * Triangle komşuluk map'i oluştur
     */
    private buildTriangleAdjacency(meshGeom: THREE.BufferGeometry): Map<number, number[]> {
        const adjacency = new Map<number, number[]>();
        const edgeToTriangles = new Map<string, number[]>();

        const triangleCount = meshGeom.index ? meshGeom.index.count / 3 : meshGeom.attributes['position'].count / 3;

        for (let i = 0; i < triangleCount; i++) {
            const i0 = meshGeom.index ? meshGeom.index.getX(i * 3) : i * 3;
            const i1 = meshGeom.index ? meshGeom.index.getX(i * 3 + 1) : i * 3 + 1;
            const i2 = meshGeom.index ? meshGeom.index.getX(i * 3 + 2) : i * 3 + 2;

            // 3 kenar
            const edges = [
                this.makeEdgeKey(i0, i1),
                this.makeEdgeKey(i1, i2),
                this.makeEdgeKey(i2, i0)
            ];

            edges.forEach(edgeKey => {
                if (!edgeToTriangles.has(edgeKey)) {
                    edgeToTriangles.set(edgeKey, []);
                }
                edgeToTriangles.get(edgeKey)!.push(i);
            });
        }

        // Komşuluk oluştur
        edgeToTriangles.forEach(triangles => {
            if (triangles.length === 2) {
                const [t1, t2] = triangles;

                if (!adjacency.has(t1)) adjacency.set(t1, []);
                if (!adjacency.has(t2)) adjacency.set(t2, []);

                adjacency.get(t1)!.push(t2);
                adjacency.get(t2)!.push(t1);
            }
        });

        return adjacency;
    }

    private makeEdgeKey(v1: number, v2: number): string {
        return v1 < v2 ? `${v1}-${v2}` : `${v2}-${v1}`;
    }

    /**
     * Loop'ları çıkarır (her face'in boundary edge'leri)
     */
    private extractLoops(brep: BrepBody): void {
        let loopId = 0;

        brep.faces.forEach(face => {
            // Face'e ait edge'leri bul (bu face'i içeren edge'ler)
            const faceEdges: string[] = [];

            brep.edges.forEach(edge => {
                if (edge.faces.includes(face.id)) {
                    faceEdges.push(edge.id);
                }
            });

            if (faceEdges.length === 0) return;

            // Edge'leri sıralı döngüye çevir
            const orderedEdges = this.orderEdgesIntoLoop(faceEdges, brep);

            if (orderedEdges.length > 0) {
                const id = `L${loopId++}`;

                // Döngüdeki vertex'leri çıkar
                const loopVertices: string[] = [];
                orderedEdges.forEach(edgeId => {
                    const edge = brep.edges.get(edgeId)!;
                    if (!loopVertices.includes(edge.startVertex)) {
                        loopVertices.push(edge.startVertex);
                    }
                });

                const loop: BrepLoop = {
                    id,
                    edges: orderedEdges,
                    isOuter: true, // İlk loop outer kabul edilir
                    vertices: loopVertices
                };

                brep.loops.set(id, loop);
                face.loops.push(loop);
                face.edges = faceEdges;
            }
        });
    }

    /**
     * Edge'leri sıralı döngüye çevir
     */
    private orderEdgesIntoLoop(edgeIds: string[], brep: BrepBody): string[] {
        if (edgeIds.length === 0) return [];

        const ordered: string[] = [];
        const remaining = new Set(edgeIds);

        // İlk edge'i al
        const firstEdgeId = edgeIds[0];
        ordered.push(firstEdgeId);
        remaining.delete(firstEdgeId);

        let currentVertex = brep.edges.get(firstEdgeId)!.endVertex;

        // Döngüyü tamamla
        while (remaining.size > 0) {
            let foundNext = false;

            for (const edgeId of remaining) {
                const edge = brep.edges.get(edgeId)!;

                if (edge.startVertex === currentVertex) {
                    ordered.push(edgeId);
                    remaining.delete(edgeId);
                    currentVertex = edge.endVertex;
                    foundNext = true;
                    break;
                } else if (edge.endVertex === currentVertex) {
                    ordered.push(edgeId);
                    remaining.delete(edgeId);
                    currentVertex = edge.startVertex;
                    foundNext = true;
                    break;
                }
            }

            if (!foundNext) break; // Döngü kapanmadı
        }

        return ordered;
    }

    /**
     * Shell'leri çıkarır (bağlı face grupları)
     */
    private extractShells(brep: BrepBody): void {
        const visited = new Set<string>();
        let shellId = 0;

        // Face komşuluk map'i oluştur (edge paylaşan face'ler)
        const faceAdjacency = new Map<string, string[]>();

        brep.edges.forEach(edge => {
            if (edge.faces.length === 2) {
                const [f1, f2] = edge.faces;

                if (!faceAdjacency.has(f1)) faceAdjacency.set(f1, []);
                if (!faceAdjacency.has(f2)) faceAdjacency.set(f2, []);

                if (!faceAdjacency.get(f1)!.includes(f2)) {
                    faceAdjacency.get(f1)!.push(f2);
                }
                if (!faceAdjacency.get(f2)!.includes(f1)) {
                    faceAdjacency.get(f2)!.push(f1);
                }
            }
        });

        // BFS ile bağlı face gruplarını bul
        brep.faces.forEach((_, faceId) => {
            if (visited.has(faceId)) return;

            const shellFaces: string[] = [];
            const queue = [faceId];

            while (queue.length > 0) {
                const current = queue.shift()!;
                if (visited.has(current)) continue;

                visited.add(current);
                shellFaces.push(current);

                const neighbors = faceAdjacency.get(current) || [];
                neighbors.forEach(n => {
                    if (!visited.has(n)) {
                        queue.push(n);
                    }
                });
            }

            if (shellFaces.length > 0) {
                const id = `S${shellId++}`;

                // Bounding box hesapla
                const boundingBox = new THREE.Box3();
                shellFaces.forEach(fId => {
                    const face = brep.faces.get(fId)!;
                    boundingBox.expandByPoint(face.centroid);
                });

                // Kapalılık kontrolü: tüm edge'ler tam 2 face'e mi bağlı
                let isClosed = true;
                const shellEdges = new Set<string>();

                shellFaces.forEach(fId => {
                    const face = brep.faces.get(fId)!;
                    face.edges.forEach(eId => shellEdges.add(eId));
                });

                shellEdges.forEach(eId => {
                    const edge = brep.edges.get(eId)!;
                    const facesInShell = edge.faces.filter(f => shellFaces.includes(f));
                    if (facesInShell.length !== 2) {
                        isClosed = false;
                    }
                });

                // Hacim hesapla (kapalı ise)
                let volume: number | undefined;
                if (isClosed) {
                    volume = this.calculateShellVolume(shellFaces, brep);
                }

                const shell: BrepShell = {
                    id,
                    faces: shellFaces,
                    isClosed,
                    volume,
                    boundingBox
                };

                brep.shells.set(id, shell);
            }
        });

        brep.stats.shellCount = brep.shells.size;
    }

    /**
     * Shell hacmi hesapla (signed volume formula)
     */
    private calculateShellVolume(faceIds: string[], brep: BrepBody): number {
        let volume = 0;

        faceIds.forEach(faceId => {
            const face = brep.faces.get(faceId)!;
            // Signed tetrahedron volume: (1/6) * (a · (b × c))
            // Origin'den face centroid'e ve face üzerindeki üçgenlere
            const centroid = face.centroid;
            const normal = face.normal;

            // Yaklaşık: face alanı × centroid'in normal yönündeki uzaklığı
            volume += (1/3) * face.area * centroid.dot(normal);
        });

        return Math.abs(volume);
    }

    /**
     * Solid'leri çıkarır (kapalı shell'ler)
     */
    private extractSolids(brep: BrepBody): void {
        let solidId = 0;

        brep.shells.forEach(shell => {
            if (!shell.isClosed) return;

            const id = `SO${solidId++}`;

            // Ağırlık merkezi hesapla
            const centerOfMass = new THREE.Vector3();
            let totalArea = 0;

            shell.faces.forEach(faceId => {
                const face = brep.faces.get(faceId)!;
                centerOfMass.add(face.centroid.clone().multiplyScalar(face.area));
                totalArea += face.area;
            });

            if (totalArea > 0) {
                centerOfMass.divideScalar(totalArea);
            }

            const solid: BrepSolid = {
                id,
                outerShell: shell.id,
                voids: [], // İç boşluklar sonra tespit edilecek
                volume: shell.volume || 0,
                centerOfMass,
                boundingBox: shell.boundingBox.clone()
            };

            brep.solids.set(id, solid);
        });

        brep.stats.solidCount = brep.solids.size;
    }

    /**
     * Yüzey tanıma (plane, cylinder, sphere, cone, torus tespit et)
     */
    private recognizeSurfaces(brep: BrepBody): void {
        const recognizer = new FikoBrepSurfaceRecognition(this.tolerance);

        brep.faces.forEach(face => {
            // Face'in vertex noktalarını topla
            const points: THREE.Vector3[] = [];

            face.loops.forEach(loop => {
                loop.vertices.forEach(vId => {
                    const vertex = brep.vertices.get(vId);
                    if (vertex) {
                        points.push(vertex.position.clone());
                    }
                });
            });

            if (points.length >= 3) {
                const surface = recognizer.recognizeSurface(face, points);
                if (surface) {
                    face.surface = surface;
                }
            }
        });
    }

    /**
     * Eğri tanıma (line, arc, circle, spline tespit et)
     * Line, Spline, Polyline BRep grup sistemine uygun
     */
    private recognizeCurves(brep: BrepBody): void {
        brep.edges.forEach(edge => {
            const startVertex = brep.vertices.get(edge.startVertex);
            const endVertex = brep.vertices.get(edge.endVertex);

            if (!startVertex || !endVertex) return;

            const start = startVertex.position;
            const end = endVertex.position;
            const length = start.distanceTo(end);

            // Mevcut curve tipine göre detaylandır
            if (edge.curve.type === 'line') {
                // Zaten line - güncelle
                edge.curve = {
                    type: 'line',
                    start: start.clone(),
                    end: end.clone(),
                    length
                };
            }

            // Arc/Circle tespiti için komşu edge'lere bak
            // Eğer 3+ ardışık edge benzer eğrilikte ise arc olabilir
            this.tryRecognizeArc(edge, brep);
        });
    }

    /**
     * Arc/Circle tanıma (3 nokta ile)
     */
    private tryRecognizeArc(edge: BrepEdge, brep: BrepBody): void {
        // Edge'in start ve end vertex'leri
        const startV = brep.vertices.get(edge.startVertex);
        const endV = brep.vertices.get(edge.endVertex);

        if (!startV || !endV) return;

        // Orta nokta için bir komşu edge bul
        const connectedEdges = startV.edges.filter(eId => eId !== edge.id);
        if (connectedEdges.length === 0) return;

        const neighborEdge = brep.edges.get(connectedEdges[0]);
        if (!neighborEdge) return;

        // 3. nokta
        const thirdVertexId = neighborEdge.startVertex === edge.startVertex
            ? neighborEdge.endVertex
            : neighborEdge.startVertex;
        const thirdV = brep.vertices.get(thirdVertexId);

        if (!thirdV) return;

        const p1 = startV.position;
        const p2 = endV.position;
        const p3 = thirdV.position;

        // 3 noktadan geçen daire
        const circle = this.fitCircleThrough3Points(p1, p2, p3);

        if (circle && circle.radius < Infinity) {
            // Tüm noktalar daire üzerinde mi kontrol et
            const dist1 = p1.distanceTo(circle.center) - circle.radius;
            const dist2 = p2.distanceTo(circle.center) - circle.radius;
            const dist3 = p3.distanceTo(circle.center) - circle.radius;

            const tolerance = this.tolerance * 10;

            if (Math.abs(dist1) < tolerance && Math.abs(dist2) < tolerance && Math.abs(dist3) < tolerance) {
                // Arc olarak işaretle
                const startAngle = Math.atan2(
                    p1.clone().sub(circle.center).dot(circle.uAxis),
                    p1.clone().sub(circle.center).dot(circle.vAxis)
                );
                const endAngle = Math.atan2(
                    p2.clone().sub(circle.center).dot(circle.uAxis),
                    p2.clone().sub(circle.center).dot(circle.vAxis)
                );

                edge.curve = {
                    type: 'arc',
                    center: circle.center,
                    radius: circle.radius,
                    startAngle,
                    endAngle,
                    normal: circle.normal,
                    length: Math.abs(endAngle - startAngle) * circle.radius
                };
            }
        }
    }

    /**
     * 3 noktadan geçen daire hesapla
     */
    private fitCircleThrough3Points(p1: THREE.Vector3, p2: THREE.Vector3, p3: THREE.Vector3): {
        center: THREE.Vector3;
        radius: number;
        normal: THREE.Vector3;
        uAxis: THREE.Vector3;
        vAxis: THREE.Vector3;
    } | null {
        // Düzlem normali
        const v1 = p2.clone().sub(p1);
        const v2 = p3.clone().sub(p1);
        const normal = v1.clone().cross(v2).normalize();

        if (normal.lengthSq() < 1e-10) return null; // Noktalar eş doğrusal

        // Düzlem üzerinde çalış (2D'ye projeksiyon)
        const uAxis = v1.clone().normalize();
        const vAxis = normal.clone().cross(uAxis).normalize();

        // 2D koordinatlar
        const a = new THREE.Vector2(0, 0);
        const b = new THREE.Vector2(v1.dot(uAxis), v1.dot(vAxis));
        const c = new THREE.Vector2(v2.dot(uAxis), v2.dot(vAxis));

        // Daire merkezi (2D)
        const d = 2 * (a.x * (b.y - c.y) + b.x * (c.y - a.y) + c.x * (a.y - b.y));
        if (Math.abs(d) < 1e-10) return null;

        const ux = ((a.x * a.x + a.y * a.y) * (b.y - c.y) +
                   (b.x * b.x + b.y * b.y) * (c.y - a.y) +
                   (c.x * c.x + c.y * c.y) * (a.y - b.y)) / d;

        const uy = ((a.x * a.x + a.y * a.y) * (c.x - b.x) +
                   (b.x * b.x + b.y * b.y) * (a.x - c.x) +
                   (c.x * c.x + c.y * c.y) * (b.x - a.x)) / d;

        const center2D = new THREE.Vector2(ux, uy);
        const radius = center2D.distanceTo(a);

        // 3D'ye geri dönüştür
        const center = p1.clone()
            .add(uAxis.clone().multiplyScalar(ux))
            .add(vAxis.clone().multiplyScalar(uy));

        return { center, radius, normal, uAxis, vAxis };
    }

    /**
     * Non-manifold geometri handler
     * Manifold olmayan edge'leri ve vertex'leri tespit edip işaretler
     */
    public handleNonManifold(brep: BrepBody): {
        nonManifoldEdges: BrepEdge[];
        nonManifoldVertices: BrepVertex[];
        boundaryEdges: BrepEdge[];
    } {
        const nonManifoldEdges: BrepEdge[] = [];
        const nonManifoldVertices: BrepVertex[] = [];
        const boundaryEdges: BrepEdge[] = [];

        // Non-manifold edge tespiti: 2'den fazla face'e bağlı edge'ler
        brep.edges.forEach(edge => {
            if (edge.faces.length > 2) {
                nonManifoldEdges.push(edge);
            } else if (edge.faces.length === 1) {
                boundaryEdges.push(edge);
            }
        });

        // Non-manifold vertex tespiti: fan yapısı bozuk vertex'ler
        brep.vertices.forEach(vertex => {
            // Vertex'e bağlı edge'lerin face'lerini kontrol et
            const connectedFaces = new Set<string>();
            vertex.edges.forEach(edgeId => {
                const edge = brep.edges.get(edgeId);
                if (edge) {
                    edge.faces.forEach(f => connectedFaces.add(f));
                }
            });

            // Face sayısı ile edge sayısı uyumsuz ise non-manifold
            // Normal durumda: edge sayısı = face sayısı (closed) veya face sayısı + 1 (boundary)
            const edgeCount = vertex.edges.length;
            const faceCount = connectedFaces.size;

            if (faceCount > edgeCount) {
                nonManifoldVertices.push(vertex);
            }
        });

        return { nonManifoldEdges, nonManifoldVertices, boundaryEdges };
    }

    /**
     * Topolojik analiz (manifold, watertight, genus, etc.)
     */
    private analyzeTopology(brep: BrepBody): MeshAnalysisResult['topology'] {
        // Manifold kontrolü: Her edge max 2 face'e bağlı olmalı
        let manifold = true;
        brep.edges.forEach(edge => {
            if (edge.faces.length > 2) manifold = false;
        });

        // Watertight kontrolü: Her edge tam 2 face'e bağlı olmalı
        let watertight = true;
        brep.edges.forEach(edge => {
            if (edge.faces.length !== 2) watertight = false;
        });

        // Euler karakteristiği: V - E + F
        const V = brep.vertices.size;
        const E = brep.edges.size;
        const F = brep.faces.size;
        const eulerCharacteristic = V - E + F;

        // Genus: g = 1 - (χ/2) (kapalı yüzey için)
        const genus = watertight ? Math.max(0, 1 - Math.floor(eulerCharacteristic / 2)) : 0;

        return {
            manifold,
            watertight,
            orientable: true, // TODO: orientability testi
            eulerCharacteristic,
            genus
        };
    }

    /**
     * Geometrik özellik analizi (convex/concave/sharp edges)
     */
    private analyzeGeometry(brep: BrepBody): MeshAnalysisResult['geometry'] {
        const convexEdges: BrepEdge[] = [];
        const concaveEdges: BrepEdge[] = [];
        const sharpEdges: BrepEdge[] = [];
        const smoothEdges: BrepEdge[] = [];

        brep.edges.forEach(edge => {
            if (edge.faces.length === 2) {
                // İki komşu face'in normallerini karşılaştır
                // TODO: angle hesaplama ve sınıflandırma

                if (edge.angle > 30) {
                    sharpEdges.push(edge);
                } else if (edge.angle < 10) {
                    smoothEdges.push(edge);
                }

                if (edge.convex) {
                    convexEdges.push(edge);
                } else {
                    concaveEdges.push(edge);
                }
            }
        });

        return {
            convexEdges,
            concaveEdges,
            sharpEdges,
            smoothEdges
        };
    }

    /**
     * Feature sınıflandırma (yüzey tiplerine göre)
     */
    private classifyFeatures(brep: BrepBody): MeshAnalysisResult['features'] {
        const planes: BrepFace[] = [];
        const cylinders: BrepFace[] = [];
        const spheres: BrepFace[] = [];
        const cones: BrepFace[] = [];
        const tori: BrepFace[] = [];
        const freeform: BrepFace[] = [];

        brep.faces.forEach(face => {
            switch (face.surface.type) {
                case 'plane': planes.push(face); break;
                case 'cylinder': cylinders.push(face); break;
                case 'sphere': spheres.push(face); break;
                case 'cone': cones.push(face); break;
                case 'torus': tori.push(face); break;
                default: freeform.push(face); break;
            }
        });

        return { planes, cylinders, spheres, cones, tori, freeform };
    }

    // ===== Yardımcı Metodlar =====

    private positionHash(pos: THREE.Vector3): string {
        const precision = Math.round(-Math.log10(this.tolerance));
        return `${pos.x.toFixed(precision)},${pos.y.toFixed(precision)},${pos.z.toFixed(precision)}`;
    }

    private positionHashByIndex(idx: number, brep: BrepBody): string {
        // TODO: position attribute'tan hash oluştur
        return '';
    }

    private findVertexByHash(hash: string, brep: BrepBody): string | undefined {
        for (const [id, vertex] of brep.vertices) {
            if (this.positionHash(vertex.position) === hash) {
                return id;
            }
        }
        return undefined;
    }

    private edgeHash(v0: string, v1: string): string {
        // Sırasız hash (v0-v1 = v1-v0)
        return v0 < v1 ? `${v0}-${v1}` : `${v1}-${v0}`;
    }
}
